import { useEffect, useRef, useState } from 'react';
import { getDescriptionForPath } from '../../config/configEntry';
import {
  clearLocalBotIcon,
  loadLocalBotIcon,
  saveLocalBotIcon,
} from './botIconLocalStorage';
import panelClasses from './AdminSettingsPanel.module.css';
import classes from './BotIconUploader.module.css';

const MAX_SVG_BYTES = 200 * 1024;
const MAX_PNG_BYTES = 500 * 1024;

export default function BotIconUploader({
  domainName,
  botName,
  iconUrl,
  originalBotIconUrl,
  onIconUrlChange,
  descriptions,
}) {
  const fileInputRef = useRef(null);
  const [uploadError, setUploadError] = useState('');
  const [hasLocalIcon, setHasLocalIcon] = useState(false);
  const [previewLabel, setPreviewLabel] = useState('');

  const label = getDescriptionForPath(descriptions, 'botIconUrl', 'botIconUrl');

  useEffect(() => {
    const local = loadLocalBotIcon(domainName, botName);
    setHasLocalIcon(Boolean(local?.url));
    if (local?.url && iconUrl === local.url) {
      setPreviewLabel('תמונה מקומית');
    }
  }, [domainName, botName, iconUrl]);

  const displayUrl = (iconUrl ?? '').trim();
  const originalUrl = (originalBotIconUrl ?? '').trim();
  const showRemove =
    hasLocalIcon ||
    (displayUrl.startsWith('data:') && displayUrl !== originalUrl);

  const onUploadClick = () => {
    setUploadError('');
    fileInputRef.current?.click();
  };

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploadError('');

    const name = String(file.name || '').toLowerCase();
    const isSvg = name.endsWith('.svg') || file.type === 'image/svg+xml';
    const isPng = name.endsWith('.png') || file.type === 'image/png';

    if (!isSvg && !isPng) {
      setUploadError('אפשר להעלות רק קובץ SVG או PNG');
      return;
    }

    let dataUrl = '';
    let mimeType = '';
    let fileName = '';

    if (isSvg) {
      if (typeof file.size === 'number' && file.size > MAX_SVG_BYTES) {
        setUploadError('הקובץ גדול מדי (מקסימום 200KB)');
        return;
      }

      let svgText = '';
      try {
        svgText = await readFileAsText(file);
      } catch {
        setUploadError('נכשלה קריאת הקובץ');
        return;
      }

      const normalizedSvg = svgText.replace(/^\uFEFF/, '').trim();
      if (!/<svg[\s>]/i.test(normalizedSvg)) {
        setUploadError('הקובץ לא נראה כמו SVG תקין');
        return;
      }

      dataUrl = svgToDataUrl(normalizedSvg);
      mimeType = 'image/svg+xml';
      fileName = String(file.name || 'icon').replace(/\.svg$/i, '');
    } else {
      if (typeof file.size === 'number' && file.size > MAX_PNG_BYTES) {
        setUploadError('הקובץ גדול מדי (מקסימום 500KB)');
        return;
      }

      try {
        dataUrl = await readFileAsDataUrl(file);
      } catch {
        setUploadError('נכשלה קריאת הקובץ');
        return;
      }

      if (!dataUrl.startsWith('data:image/png')) {
        setUploadError('הקובץ לא נראה כמו PNG תקין');
        return;
      }

      mimeType = 'image/png';
      fileName = String(file.name || 'icon').replace(/\.png$/i, '');
    }

    const saved = saveLocalBotIcon(domainName, botName, { url: dataUrl, mimeType, fileName });
    if (!saved) {
      setUploadError('לא ניתן לשמור (ייתכן ש-localStorage מלא)');
      return;
    }

    setHasLocalIcon(true);
    setPreviewLabel(fileName || 'תמונה מקומית');
    onIconUrlChange(dataUrl);
  };

  const onRemoveLocal = () => {
    clearLocalBotIcon(domainName, botName);
    setHasLocalIcon(false);
    setPreviewLabel('');
    setUploadError('');
    onIconUrlChange(originalBotIconUrl ?? '');
  };

  return (
    <div className={classes.field}>
      <label className={panelClasses.label}>{label}</label>
      {displayUrl ? (
        <div className={classes.previewRow}>
          <img src={displayUrl} alt="" />
          <span>{previewLabel || (hasLocalIcon ? 'תמונה מקומית' : 'תמונת בוט')}</span>
        </div>
      ) : null}
      <div className={classes.actions}>
        <button type="button" className={panelClasses.btn} onClick={onUploadClick}>
          העלה תמונת בוט (SVG / PNG)
        </button>
        {showRemove ? (
          <button type="button" className={panelClasses.btn} onClick={onRemoveLocal}>
            הסר תמונה מקומית
          </button>
        ) : null}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/svg+xml,.svg,image/png,.png"
          style={{ display: 'none' }}
          onChange={onUploadFile}
        />
      </div>
      {uploadError ? <p className={classes.hintError}>{uploadError}</p> : null}
    </div>
  );
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsText(file);
  });
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsDataURL(file);
  });
}

function svgToDataUrl(svgText) {
  return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgText)))}`;
}
